import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Heading,
  Button,
  Card,
  CardBody,
  CardHeader,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Switch,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Divider,
  Alert,
  AlertIcon,
  useToast,
  Skeleton,
} from '@chakra-ui/react';
import { FiSave, FiArrowLeft } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { useVoiceService, VoiceRoom } from '../../hooks/useVoiceService';
import { useUserProfile } from '../../contexts/userprofile/UserProfileContext';

export const VoiceRoomSettingsPage: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const [room, setRoom] = useState<VoiceRoom | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    type: 1,
    settings: {
      max_groups: 10,
      is_public: true,
      allow_guest_groups: false,
      auto_archive_empty_groups: true,
      auto_archive_days: 30,
      max_created_groups_per_user: 5,
      max_owned_groups_per_user: 3,
      default_group_settings: {
        max_members: 20,
        is_public: true,
        allow_guest: false,
        audio_quality: 2,
      },
    },
  });
  
  const { profile } = useUserProfile();
  const navigate = useNavigate();
  const toast = useToast();
  const { getRoom, updateRoom } = useVoiceService();

  const loadRoom = useCallback(async () => {
    if (!roomId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await getRoom(roomId);
      const roomData = response.room;
      setRoom(roomData);
      
      // Populate form with room data
      setFormData({
        name: roomData.name || '',
        description: roomData.description || '',
        type: roomData.type || 1,
        settings: {
          max_groups: roomData.settings?.max_groups || 10,
          is_public: roomData.settings?.is_public || true,
          allow_guest_groups: roomData.settings?.allow_guest_groups || false,
          auto_archive_empty_groups: roomData.settings?.auto_archive_empty_groups || true,
          auto_archive_days: roomData.settings?.auto_archive_days || 30,
          max_created_groups_per_user: roomData.settings?.max_created_groups_per_user || 5,
          max_owned_groups_per_user: roomData.settings?.max_owned_groups_per_user || 3,
          default_group_settings: {
            max_members: roomData.settings?.default_group_settings?.max_members || 20,
            is_public: roomData.settings?.default_group_settings?.is_public || true,
            allow_guest: roomData.settings?.default_group_settings?.allow_guest || false,
            audio_quality: roomData.settings?.default_group_settings?.audio_quality || 2,
          },
        },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to load room');
    } finally {
      setLoading(false);
    }
  }, [roomId, getRoom]);

  useEffect(() => {
    loadRoom();
  }, [loadRoom]);

  const handleSave = async () => {
    if (!roomId) return;
    
    try {
      setSaving(true);
      await updateRoom(roomId, formData);
      toast({
        title: 'Room settings updated',
        status: 'success',
        duration: 3000,
      });
      navigate(`/voice/rooms/${roomId}`);
    } catch (err: any) {
      toast({
        title: 'Failed to update room settings',
        description: err.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setSaving(false);
    }
  };

  // Check if user is room owner
  console.log('Room Owner ID:', room?.owner?.id);
  console.log('UserProfile ID:', profile?.id);
  const isOwner = room?.owner?.id === profile?.id;

  // Update left sidebar
  useEffect(() => {
    const sidebarItems = [
      {
        label: 'All Rooms',
        path: '/voice/rooms',
      },
      {
        label: room?.name || 'Room',
        path: `/voice/rooms/${roomId}`,
      },
      {
        label: 'Settings',
        path: `/voice/rooms/${roomId}/settings`,
        active: true,
      },
    ];

    window.updateLeftSidebar?.(sidebarItems);

    return () => window.updateLeftSidebar?.([]);
  }, [roomId, room?.name]);

  if (loading) {
    return (
      <Box p={6}>
        <Skeleton height="40px" mb={6} />
        <Skeleton height="400px" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={6}>
        <Alert status="error">
          <AlertIcon />
          {error}
        </Alert>
      </Box>
    );
  }

  if (!isOwner) {
    return (
      <Box p={6}>
        <Alert status="warning">
          <AlertIcon />
          Only room owners can modify settings
        </Alert>
        <Button mt={4} leftIcon={<FiArrowLeft />} onClick={() => navigate(`/voice/rooms/${roomId}`)}>
          Back to Room
        </Button>
      </Box>
    );
  }

  return (
    <Box p={6}>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Room Settings</Heading>
        <HStack>
          <Button variant="outline" leftIcon={<FiArrowLeft />} onClick={() => navigate(`/voice/rooms/${roomId}`)}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            leftIcon={<FiSave />} 
            onClick={handleSave}
            isLoading={saving}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </HStack>
      </HStack>

      <Card>
        <CardHeader>
          <Heading size="md">Basic Information</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <FormControl isRequired>
              <FormLabel>Room Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Enter room name"
              />
            </FormControl>

            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Enter room description"
                rows={3}
              />
            </FormControl>

            <FormControl>
              <FormLabel>Room Type</FormLabel>
              <Select
                value={formData.type}
                onChange={(e) => setFormData(prev => ({ ...prev, type: parseInt(e.target.value) }))}
              >
                <option value={1}>Standard</option>
                <option value={2}>Workspace</option>
                <option value={3}>Event</option>
                <option value={4}>Gaming</option>
              </Select>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      <Card mt={6}>
        <CardHeader>
          <Heading size="md">Room Settings</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack>
              <FormControl>
                <FormLabel>Max Groups</FormLabel>
                <NumberInput
                  value={formData.settings.max_groups}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_groups: value || 10 }
                  }))}
                  min={1}
                  max={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Auto Archive Days</FormLabel>
                <NumberInput
                  value={formData.settings.auto_archive_days}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, auto_archive_days: value || 30 }
                  }))}
                  min={1}
                  max={365}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <HStack>
              <FormControl>
                <FormLabel>Max Groups Per User</FormLabel>
                <NumberInput
                  value={formData.settings.max_created_groups_per_user}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_created_groups_per_user: value || 5 }
                  }))}
                  min={1}
                  max={50}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Max Owned Groups Per User</FormLabel>
                <NumberInput
                  value={formData.settings.max_owned_groups_per_user}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: { ...prev.settings, max_owned_groups_per_user: value || 3 }
                  }))}
                  min={1}
                  max={20}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
            </HStack>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="is-public" mb="0">
                Public Room
              </FormLabel>
              <Switch
                id="is-public"
                isChecked={formData.settings.is_public}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, is_public: e.target.checked }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="allow-guest" mb="0">
                Allow Guest Groups
              </FormLabel>
              <Switch
                id="allow-guest"
                isChecked={formData.settings.allow_guest_groups}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, allow_guest_groups: e.target.checked }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="auto-archive" mb="0">
                Auto Archive Empty Groups
              </FormLabel>
              <Switch
                id="auto-archive"
                isChecked={formData.settings.auto_archive_empty_groups}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: { ...prev.settings, auto_archive_empty_groups: e.target.checked }
                }))}
              />
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      <Card mt={6}>
        <CardHeader>
          <Heading size="md">Default Group Settings</Heading>
        </CardHeader>
        <CardBody>
          <VStack spacing={4} align="stretch">
            <HStack>
              <FormControl>
                <FormLabel>Max Members</FormLabel>
                <NumberInput
                  value={formData.settings.default_group_settings.max_members}
                  onChange={(_, value) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      default_group_settings: {
                        ...prev.settings.default_group_settings,
                        max_members: value || 20
                      }
                    }
                  }))}
                  min={1}
                  max={100}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Audio Quality</FormLabel>
                <Select
                  value={formData.settings.default_group_settings.audio_quality}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    settings: {
                      ...prev.settings,
                      default_group_settings: {
                        ...prev.settings.default_group_settings,
                        audio_quality: parseInt(e.target.value)
                      }
                    }
                  }))}
                >
                  <option value={1}>Low</option>
                  <option value={2}>High</option>
                  <option value={3}>Ultra</option>
                </Select>
              </FormControl>
            </HStack>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="default-public" mb="0">
                Groups Public by Default
              </FormLabel>
              <Switch
                id="default-public"
                isChecked={formData.settings.default_group_settings.is_public}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    default_group_settings: {
                      ...prev.settings.default_group_settings,
                      is_public: e.target.checked
                    }
                  }
                }))}
              />
            </FormControl>

            <FormControl display="flex" alignItems="center">
              <FormLabel htmlFor="default-guest" mb="0">
                Allow Guests by Default
              </FormLabel>
              <Switch
                id="default-guest"
                isChecked={formData.settings.default_group_settings.allow_guest}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  settings: {
                    ...prev.settings,
                    default_group_settings: {
                      ...prev.settings.default_group_settings,
                      allow_guest: e.target.checked
                    }
                  }
                }))}
              />
            </FormControl>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};